/** @jsxImportSource @emotion/react */

import React, { useEffect, useRef, useState } from 'react';
import { getAttendanceType } from '@util/attendance';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { ETheme } from '@datatype/type';
import {
  EApiAttendanceType,
  EApiLanguageCode,
  EApiStudyType,
  type IClassroomConversationProblem,
} from '@datatype/iyagigo/api-type';
import TextBlock from '@component/common/block/text-block';
import TextButton from '@design-system/text-button';
// import Canvas from '@design-system/canvas';
import Chip from '@design-system/chip';
import OtterTeacher, { type IPlaybackState } from '@component/common/otter-teacher';
import RemotionWatchBoard from '@component/common/remotion-watch-board';
import WatchControl from '@component/iyagigo/classroom/grammar-study/lecture-controls';
import BottomContainer from '@component/common/architecture/bottom-container';
import Scroll, { ScrollContainer } from '@component/common/scroll';
import Gap from '@component/common/gap';
import TextBubble from '@design-system/text-bubble';
import StudyNote from '@component/iyagigo/common/study-note';
import IconTextBlock from '@component/common/block/icon-text-block';
import IconBlock from '@component/common/block/icon-block';
import { usePlaySound } from '@hook/common/useSound';
import { useAudioPlayer } from '@hook/common/useAudioPlayer';
import { useActiveTime } from '@hook/common/useEvent';
import { useTypedDispatch } from '@hook/common/useStore';
import { useCheckAttendance, useRecordStudy } from '@hook/iyagigo/useStudent';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { LOCAL_STORAGE_STUDENT_KEY } from '@config/auth';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import { actions as modalActions } from '@store/slice/modal-slice';
import { shuffleArrayWithSeed } from '@util/random';
import { addFurigana, createFuriganaMap, normalizeExplanation } from '@util/string';
import {
  useModifyClassroomConversationWorkbookMutation,
  useClassroomConversationWorkbooksQuery,
} from '@hook/iyagigo/useApi';

// ###################################
// 생활회화 학습
// - watch → read → practice 플로우.
// - watch는 백엔드 seed가 생성한 TTS와 scene.at 타임라인을 사용한다.
// ###################################

type TStudyPhase = 'watch' | 'read' | 'practice';

const toBubbleFurigana = (text: string, pronunciation: string | null | undefined, languageCode: EApiLanguageCode) => {
  if (languageCode !== EApiLanguageCode.JAPANESE || !pronunciation) return text;

  const furiganaMap = createFuriganaMap(text, pronunciation);
  Array.from(furiganaMap.keys()).forEach((notation) => {
    if (/^\d+$/.test(notation)) furiganaMap.delete(notation);
  });

  return addFurigana(text, furiganaMap, 'start');
};

const getSpeakerProfileEmoji = (gender?: string | null) => {
  if (gender === '남' || gender === 'male') return '🧑🏻';
  if (gender === '여' || gender === 'female') return '👩🏻';
  return '🙂';
};

interface IProps {
  onProgress?: React.Dispatch<{ current: number; total: number }>;
  onTitleChange?: React.Dispatch<string>;
}

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  height: 100%;
  padding: ${size.PAGE_PADDING} ${size.PAGE_PADDING} 0;
`;

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  flex-flow: column nowrap;
  width: 100%;
`;

const WatchContainer = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-flow: column nowrap;
  width: 100%;
  overflow: hidden;
  border-radius: ${size.MARGIN_2};
`;

const TeacherContainer = styled.div`
  flex-shrink: 0;
  width: 100%;
`;

const BottomContentContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: 100%;
  gap: ${size.MARGIN_1};
  padding: 0 ${size.PAGE_PADDING} ${IS_IYAGIGO_MOBILE_VIEW ? '0px' : size.MARGIN_4} ${size.PAGE_PADDING};
`;

const NavigationContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const ControlRowContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  width: 100%;
  height: 35px; // medium TextButton 높이
`;

const NAVIGATION_BUTTON_WIDTH = '80px';

const ReadContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  width: 100%;
`;

// const ExpressionContainer = styled.div`
//   display: flex;
//   flex-flow: column nowrap;
//   align-items: flex-start;
//   gap: 5px;
//   width: 100%;
// `;

const ReadConversationContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${size.MARGIN_2};
  width: 100%;
  padding: ${size.MARGIN_2};
  border-radius: ${size.BORDER_RADIUS_2};
  background-color: ${color.BROWN_5};
`;

const ReadLineControlContainer = styled.div<{ $isUser: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  align-items: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  width: 100%;
`;

const ReadLineControlRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${size.MARGIN_1};
  margin-top: 5px;
`;

const ReadLineMeaningContainer = styled.div`
  width: 100%;
  margin-top: 4px;
  padding: ${size.MARGIN_1};
  border: ${size.BORDER_WIDTH_1} solid ${color.GRAY_1};
  border-radius: ${size.BORDER_RADIUS_1};
  background-color: ${color.WHITE_1};
`;

const HiddenAudio = styled.audio`
  display: none;
`;

interface IReadLineControlProps {
  audioLink: string | null;
  translation: string;
  explanation: string;
  isUser: boolean;
  isMeaningVisibleByDefault?: boolean;
}

const ReadLineControl: React.FC<IReadLineControlProps> = ({
  audioLink,
  translation,
  explanation,
  isUser,
  isMeaningVisibleByDefault = false,
}) => {
  const [isMeaningVisible, setIsMeaningVisible] = useState(isMeaningVisibleByDefault);
  const { isPlaying, audioRefs, handlePlayCompleted, handlePlayAndPauseClick } = useAudioPlayer({
    sources: audioLink ? [audioLink] : [],
  });

  return (
    <ReadLineControlContainer $isUser={isUser}>
      <ReadLineControlRow>
        {audioLink && (
          <>
            <IconBlock
              icon={isPlaying ? 'volume-off' : 'volume-on'}
              design={{
                icon: { size: 'small', color: color.GRAY_1, kind: 'outlined' },
                box: { event: { cursor: 'pointer' } },
              }}
              click={{ handler: handlePlayAndPauseClick }}
            />
            <HiddenAudio
              ref={(element) => {
                if (element) audioRefs.current[0] = element;
              }}
              src={audioLink}
              onEnded={handlePlayCompleted}
            />
          </>
        )}
        {(translation || explanation) && (
          <IconBlock
            icon={isMeaningVisible ? 'text-snippet-off' : 'text-snippet'}
            design={{
              icon: { size: 'small', color: color.GRAY_1, kind: 'outlined' },
              box: { event: { cursor: 'pointer' } },
            }}
            click={{ handler: () => setIsMeaningVisible((visible) => !visible) }}
          />
        )}
      </ReadLineControlRow>
      {isMeaningVisible && translation && (
        <ReadLineMeaningContainer>
          <TextBlock
            text={normalizeExplanation(translation, color.BLACK_2)}
            design={{
              text: { size: 'small', color: color.BLACK_2 },
              box: { alignX: 'left' },
            }}
          />
          {explanation && (
            <>
              <Gap height='5px' />
              <TextBlock
                text={normalizeExplanation(explanation, color.GRAY_1)}
                design={{
                  text: { size: 'small', color: color.GRAY_1 },
                  box: { alignX: 'left' },
                }}
              />
            </>
          )}
        </ReadLineMeaningContainer>
      )}
    </ReadLineControlContainer>
  );
};

const PracticeContainer = styled.div`
  display: flex;
  flex: 1;
  flex-flow: column nowrap;
  align-items: center;
  width: 100%;
`;

const SituationContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
`;

const ConversationContainer = styled.div`
  display: flex;
  flex: 1;
  flex-flow: column nowrap;
  gap: ${size.MARGIN_2};
  width: 100%;
  min-height: 230px;
  padding: ${size.MARGIN_2};
  border-radius: ${size.BORDER_RADIUS_2};
  background-color: ${color.BROWN_5};
`;

const ChoicesContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 8px;
  width: 100%;
`;

const ChoiceContainer = styled.button<{ $borderColor: string; $answered: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  padding: 5px;
  border: 1px solid ${({ $borderColor }) => $borderColor};
  border-radius: ${size.BORDER_RADIUS_1};
  background-color: ${color.WHITE_1};
  color: ${color.BLACK_2};
  cursor: ${({ $answered }) => ($answered ? 'default' : 'pointer')};
`;

const getChoiceBorderColor = (isAnswerView: boolean, isSelected: boolean, isCorrect: boolean): string => {
  if (isAnswerView) {
    if (isCorrect) return color.GREEN_4;
    if (isSelected) return color.RED_3;
  } else if (isSelected) {
    return color.YELLOW_3;
  }
  return color.GRAY_4;
};

const ClassroomConversationStudy: React.FC<IProps> = ({ onProgress, onTitleChange }) => {
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();
  const location = useLocation();
  const playSound = usePlaySound();
  const checkAttendance = useCheckAttendance();
  const recordStudy = useRecordStudy();
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
  const conversationKey = new URLSearchParams(location.search).get('subject');
  const languageCode =
    new URLSearchParams(location.search).get('language')?.toLowerCase() === 'en'
      ? EApiLanguageCode.ENGLISH
      : EApiLanguageCode.JAPANESE;
  const { data: workbooksResponse, isLoading } = useClassroomConversationWorkbooksQuery(languageCode);
  const modifyWorkbookMutation = useModifyClassroomConversationWorkbookMutation(languageCode);
  const workbooks = workbooksResponse?.data ?? [];
  const expressions = workbooks.map((workbook) => workbook.practicalConversation);
  const expression = expressions.find((item) => item.conversationKey === conversationKey) ?? null;
  const workbook = workbooks.find((item) => item.practicalConversation.conversationKey === conversationKey);

  const [phase, setPhase] = useState<TStudyPhase>('watch');
  const { getActiveTime, reset: resetActiveTime } = useActiveTime(true, phase === 'watch' ? 60_000 : 10_000);
  const [problemIndex, setProblemIndex] = useState(0);
  const [selectedChoiceKey, setSelectedChoiceKey] = useState<string | null>(null);
  const [isAnswerView, setIsAnswerView] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const choiceShuffleSeedRef = useRef(Math.random().toString(36));
  const [watchPlayback, setWatchPlayback] = useState<IPlaybackState>({
    currentTime: 0,
    duration: 0,
    isPlaying: false,
    playbackRate: 1,
  });
  const watchAudioRef = useRef<HTMLAudioElement>(null);
  useEffect(() => {
    if (phase !== 'watch') return;
    const timer = window.setInterval(() => {
      const studyTime = getActiveTime();
      if (!studentKey || studyTime <= 0) return;
      recordStudy({
        studentKey,
        studyType: EApiStudyType.CLASSROOM_CONVERSATION,
        languageCode: languageCode,
        studyTime,
      });
      resetActiveTime(false);
    }, 10_000);
    return () => window.clearInterval(timer);
  }, [phase, studentKey, languageCode, getActiveTime, resetActiveTime, recordStudy]);

  const readExpressions = expression?.read?.expression ?? [];
  const readConversation = expression?.read?.conversation ?? null;
  const conversationNote = expression?.read?.conversationNote ?? null;
  const problems: IClassroomConversationProblem[] = expression?.practice?.problem ?? [];
  const currentProblem = problems[problemIndex] ?? null;
  const currentLines = currentProblem?.line ?? [];
  const currentChoices = currentProblem
    ? shuffleArrayWithSeed(currentProblem.choice, `${choiceShuffleSeedRef.current}-${currentProblem.key}`)
    : [];
  const hasWatch = Boolean(expression?.watch?.audioLink);
  const hasRead = readExpressions.length > 0;
  const studyStepCount = (hasWatch ? 1 : 0) + (hasRead ? 1 : 0);
  const totalSteps = studyStepCount + problems.length;
  const currentStep = phase === 'watch' ? 0 : phase === 'read' ? (hasWatch ? 1 : 0) : studyStepCount + problemIndex;

  const watch = expression?.watch ?? null;

  const syncWatchPlayback = () => {
    const audio = watchAudioRef.current;
    if (!audio) return;
    setWatchPlayback({
      currentTime: audio.currentTime,
      duration: Number.isFinite(audio.duration) ? audio.duration : 0,
      isPlaying: !audio.paused,
      playbackRate: audio.playbackRate,
    });
  };

  useEffect(() => {
    onProgress?.({ current: currentStep, total: totalSteps });
  }, [currentStep, onProgress, totalSteps]);

  useEffect(() => {
    if (expression?.conversationName?.ko) onTitleChange?.(expression.conversationName.ko);
  }, [expression, onTitleChange]);

  useEffect(() => {
    if (!isLoading && !expression) navigate(IYAGIGO_PATH.CLASSROOM_HOME_PATH, { replace: true });
  }, [expression, isLoading, navigate]);

  useEffect(() => {
    if (!isLoading && expression && !hasWatch && phase === 'watch') {
      setPhase(hasRead ? 'read' : 'practice');
    }
  }, [expression, hasRead, hasWatch, isLoading, phase]);

  if (!expression) return null;

  const showCompleteModal = () => {
    if (workbook && !workbook.studiedAt) {
      modifyWorkbookMutation.mutate({ workbookKey: workbook.key, isStudy: true });
    }
    checkAttendance(getAttendanceType(languageCode, 'conversation', 'classroom'));
    const incorrectCount = problems.length - correctCount;
    dispatch(
      modalActions.showContentModal({
        type: 'study-celebration',
        data: {
          title: '오늘도 해냈어요!',
          description: `<strong>${expression.conversationName?.ko ?? ''}</strong> 학습을 끝까지 마쳤어요`,
        },
      }),
    );
  };

  const handleOpenRead = () => {
    recordStudy({
      studentKey,
      studyType: EApiStudyType.CLASSROOM_CONVERSATION,
      languageCode: languageCode,
      studyCount: 1,
    });
    resetActiveTime();
    watchAudioRef.current?.pause();
    if (hasRead) {
      setPhase('read');
    } else if (problems.length > 0) {
      setPhase('practice');
    } else {
      showCompleteModal();
    }
  };

  const handleOpenWatch = () => {
    if (watchAudioRef.current) watchAudioRef.current.currentTime = 0;
    setPhase('watch');
  };

  const handleStartPractice = () => {
    recordStudy({
      studentKey,
      studyType: EApiStudyType.CLASSROOM_CONVERSATION,
      languageCode,
      studyTime: getActiveTime(),
    });
    resetActiveTime();
    if (problems.length === 0) {
      showCompleteModal();
      return;
    }
    setPhase('practice');
  };

  const handleScoring = () => {
    if (!currentProblem || selectedChoiceKey === null || isAnswerView) return;
    recordStudy({
      studentKey,
      studyType: EApiStudyType.CLASSROOM_CONVERSATION,
      languageCode,
      studyTime: getActiveTime(),
    });
    resetActiveTime();
    const selectedChoice = currentChoices.find((choice) => choice.key === selectedChoiceKey);
    const isCorrect = selectedChoice?.correct ?? false;
    setIsAnswerView(true);
    playSound(isCorrect ? 'correct' : 'incorrect');
    if (isCorrect) setCorrectCount((count) => count + 1);
  };

  const handleNextProblem = () => {
    if (problemIndex >= problems.length - 1) {
      showCompleteModal();
      return;
    }
    setProblemIndex((index) => index + 1);
    setSelectedChoiceKey(null);
    setIsAnswerView(false);
  };

  const ReadConversationBubbles = readConversation?.line.map((line, index) => {
    const speaker = readConversation.speaker?.find((item) => item.id === line.speaker);
    const isUser = speaker?.name === '나';

    return (
      <TextBubble
        key={`${line.speaker}-${line.text}-${index}`}
        type='plain'
        text={toBubbleFurigana(line.text, line.pronunciation, languageCode)}
        theme={isUser ? ETheme.BROWN : ETheme.GRAY}
        size='medium'
        align={isUser ? 'right' : 'left'}
        speakerName={speaker?.name ?? line.speaker}
        profileEmoji={getSpeakerProfileEmoji(speaker?.gender)}
        lineMaxWidth='calc(100% - 50px)'
        control={
          <ReadLineControl
            audioLink={line.audioLink}
            translation={line.translation?.ko ?? ''}
            explanation={line.explanation?.ko ?? ''}
            isUser={isUser}
          />
        }
      />
    );
  });

  const Choices = currentChoices.map((choice) => {
    const isSelected = selectedChoiceKey === choice.key;
    return (
      <ChoiceContainer
        key={choice.key}
        type='button'
        $borderColor={getChoiceBorderColor(isAnswerView, isSelected, choice.correct)}
        $answered={isAnswerView}
        onClick={() => {
          if (!isAnswerView) setSelectedChoiceKey(choice.key);
        }}>
        <TextBlock
          text={choice.notation}
          annotation={
            languageCode === EApiLanguageCode.JAPANESE &&
            choice.pronunciation &&
            choice.pronunciation !== choice.notation
              ? { reading: choice.pronunciation }
              : undefined
          }
          design={{
            text: { size: 'large', color: color.BLACK_2 },
            box: { alignX: 'left', width: '100%' },
          }}
        />
      </ChoiceContainer>
    );
  });

  const selectedChoice = currentChoices.find((choice) => choice.key === selectedChoiceKey);
  const correctChoice = currentChoices.find((choice) => choice.correct);
  const isSelectedCorrect = selectedChoice?.correct ?? false;
  const isLastProblem = problemIndex === problems.length - 1;
  const answerIdx = currentProblem
    ? Math.min(Math.max(currentProblem.answerIdx ?? 0, 0), Math.max(currentLines.length - 1, 0))
    : 0;
  const answerLine = currentLines[answerIdx];
  const answerSpeaker = currentProblem?.speaker?.find((speaker) => speaker.id === answerLine?.speaker);
  const userSpeakerId = answerLine?.speaker;
  const AnswerBubble = (
    <TextBubble
      type='plain'
      text={
        isAnswerView && correctChoice
          ? toBubbleFurigana(answerLine?.text ?? '', answerLine?.pronunciation, languageCode)
          : '빈칸을 채워 주세요'
      }
      animatedText={!isAnswerView}
      theme={ETheme.BROWN}
      size='medium'
      align='right'
      speakerName={answerSpeaker?.name ?? answerLine?.speaker ?? ''}
      profileEmoji={getSpeakerProfileEmoji(answerSpeaker?.gender)}
      lineMaxWidth='calc(100% - 50px)'
      control={
        isAnswerView && correctChoice && answerLine ? (
          <ReadLineControl
            audioLink={answerLine.audioLink}
            translation={answerLine.translation?.ko ?? ''}
            explanation=''
            isUser
            isMeaningVisibleByDefault
          />
        ) : undefined
      }
    />
  );
  const ConversationBubbles = currentProblem
    ? currentLines.map((line, index) => {
        if (index === answerIdx) {
          return <React.Fragment key={`${currentProblem.key}-answer`}>{AnswerBubble}</React.Fragment>;
        }

        const speaker = currentProblem.speaker?.find((item) => item.id === line.speaker);
        const isUser = line.speaker === userSpeakerId;
        return (
          <TextBubble
            key={`${currentProblem.key}-line-${index}`}
            type='plain'
            text={toBubbleFurigana(line.text, line.pronunciation, languageCode)}
            theme={isUser ? ETheme.BROWN : ETheme.GRAY}
            size='medium'
            align={isUser ? 'right' : 'left'}
            speakerName={speaker?.name ?? line.speaker}
            profileEmoji={getSpeakerProfileEmoji(speaker?.gender)}
            lineMaxWidth='calc(100% - 50px)'
            control={
              <ReadLineControl
                audioLink={line.audioLink}
                translation={line.translation?.ko ?? ''}
                explanation=''
                isUser={isUser}
              />
            }
          />
        );
      })
    : null;

  return (
    <Wrapper>
      <ScrollContainer>
        <Scroll key={phase} direction='y' height='100%'>
          <ContentContainer>
            {phase === 'watch' && watch?.audioLink && (
              <>
                {/* Web Audio 립싱크 복원 시 crossOrigin='anonymous'와 서버 CORS 허용을 함께 검토한다. */}
                <audio
                  ref={watchAudioRef}
                  src={watch.audioLink}
                  preload='metadata'
                  style={{ display: 'none' }}
                  onLoadedMetadata={syncWatchPlayback}
                  onTimeUpdate={syncWatchPlayback}
                  onPlay={syncWatchPlayback}
                  onPause={syncWatchPlayback}
                  onRateChange={syncWatchPlayback}
                  onEnded={syncWatchPlayback}
                />
                <WatchContainer>
                  <TeacherContainer>
                    <OtterTeacher
                      audioRef={watchAudioRef}
                      onClick={() => {
                        const audio = watchAudioRef.current;
                        if (!audio) return;
                        if (audio.paused) void audio.play().catch(() => undefined);
                        else audio.pause();
                      }}
                    />
                  </TeacherContainer>
                  <RemotionWatchBoard
                    class='conversation'
                    watch={watch}
                    playback={watchPlayback}
                    contentKey={expression.conversationKey}
                    languageCode={languageCode}
                  />
                </WatchContainer>
              </>
            )}

            {phase === 'read' && (
              <ReadContainer>
                {/* <Canvas theme={ETheme.BROWN} shape='round' width='100%'>
                    <ExpressionContainer>
                      <Chip
                        text={expression.conversationName?.ko ?? ''}
                        size='small'
                        theme={ETheme.BROWN}
                        shape='round'
                        usage='static'
                        kind='filled'
                        width='fit-content'
                      />
                      <TextBlock
                        text='이 회화를 배워요'
                        design={{ text: { size: 'medium', color: color.BROWN_2, bold: true } }}
                      />
                      <TextBlock
                        text={normalizeExplanation(expression.description?.ko ?? undefined, color.BLACK_2)}
                        design={{ text: { size: 'medium', color: color.BLACK_2 }, box: { alignX: 'left' } }}
                      />
                      {expression.read?.note?.ko && (
                        <>
                          <Gap height={size.MARGIN_1} />
                          <TextBlock
                            text='알아두기'
                            design={{ text: { size: 'medium', color: color.BROWN_2, bold: true } }}
                          />
                          <TextBlock
                            text={normalizeExplanation(expression.read.note.ko, color.BLACK_4)}
                            design={{ text: { size: 'small', color: color.BLACK_4 }, box: { alignX: 'left' } }}
                          />
                        </>
                      )}
                    </ExpressionContainer>
                  </Canvas> */}
                {readConversation && (
                  <>
                    <IconTextBlock
                      icon='feed'
                      text='대화로 익혀요'
                      arrangement='icon-text'
                      design={{
                        icon: { kind: 'outlined', size: 'small', color: color.BLACK_2 },
                        text: { size: 'large', color: color.BLACK_2, bold: true },
                        box: { alignX: 'left', gap: '6px' },
                      }}
                    />
                    <Gap height={size.MARGIN_1} />
                    <ReadConversationContainer>
                      <SituationContainer>
                        <Chip
                          text={readConversation.situation}
                          size='small'
                          theme={ETheme.BROWN}
                          shape='round'
                          usage='static'
                          kind='filled'
                          width='100%'
                        />
                      </SituationContainer>
                      {ReadConversationBubbles}
                    </ReadConversationContainer>
                  </>
                )}
                {conversationNote &&
                  ((conversationNote.vocabulary?.length ?? 0) > 0 || (conversationNote.grammar?.length ?? 0) > 0) && (
                    <>
                      <Gap height={size.MARGIN_2} />
                      <IconTextBlock
                        icon='book'
                        text='대화에서 짚어볼 어휘·문법'
                        arrangement='icon-text'
                        design={{
                          icon: { kind: 'outlined', size: 'small', color: color.BLACK_2 },
                          text: { size: 'medium', color: color.BLACK_2, bold: true, underline: true },
                          box: { alignX: 'left', gap: '6px' },
                        }}
                      />
                      <Gap height={'5px'} />
                      <StudyNote vocabularies={conversationNote.vocabulary} grammars={conversationNote.grammar} />
                    </>
                  )}
              </ReadContainer>
            )}

            {phase === 'practice' && currentProblem && (
              <PracticeContainer>
                <ConversationContainer>
                  <SituationContainer>
                    <Chip
                      text={currentProblem.situation}
                      size='small'
                      theme={ETheme.BROWN}
                      shape='round'
                      usage='static'
                      kind='filled'
                      width='100%'
                    />
                  </SituationContainer>
                  {ConversationBubbles}
                </ConversationContainer>
              </PracticeContainer>
            )}
          </ContentContainer>
        </Scroll>
      </ScrollContainer>
      <Gap height={size.MARGIN_1} />
      <BottomContainer
        position='relative'
        height='auto'
        backgroundColor={color.WHITE_1}
        margin={`0 -${size.PAGE_PADDING}`}
        animationKey={phase === 'practice' ? (isAnswerView ? 'answer' : 'question') : undefined}>
        <BottomContentContainer>
          {phase === 'watch' && watch?.audioLink && (
            <>
              <WatchControl audioRef={watchAudioRef} playback={watchPlayback} />
              <TextButton
                text='다음으로'
                theme={ETheme.BROWN}
                size='medium'
                shape='pill'
                width='100%'
                handleClick={handleOpenRead}
              />
            </>
          )}

          {phase === 'read' && (
            <NavigationContainer>
              <TextButton
                text={problems.length > 0 ? '연습하기' : '학습 완료'}
                theme={ETheme.BROWN}
                size='medium'
                shape='round'
                width={hasWatch ? 'calc(100% - 90px)' : '100%'}
                handleClick={handleStartPractice}
              />
              {hasWatch && (
                <TextButton
                  text='이전으로'
                  theme={ETheme.BROWN}
                  kind='outlined'
                  size='medium'
                  shape='square'
                  width='80px'
                  handleClick={handleOpenWatch}
                />
              )}
            </NavigationContainer>
          )}

          {phase === 'practice' && (
            <>
              <ChoicesContainer>{Choices}</ChoicesContainer>
              <ControlRowContainer>
                {isAnswerView && (
                  <TextButton
                    text={isLastProblem ? '끝내기' : '다음 문제'}
                    theme={ETheme.BROWN}
                    size='medium'
                    shape='square'
                    width={NAVIGATION_BUTTON_WIDTH}
                    handleClick={handleNextProblem}
                  />
                )}
              </ControlRowContainer>
              {isAnswerView ? (
                <TextButton
                  text={isSelectedCorrect ? '정답이에요' : '오답이에요'}
                  icon={isSelectedCorrect ? 'circle' : 'close'}
                  theme={isSelectedCorrect ? ETheme.GREEN : ETheme.RED}
                  size='medium'
                  shape='pill'
                  width='100%'
                />
              ) : (
                <TextButton
                  text='채점하기'
                  theme={ETheme.BROWN}
                  size='medium'
                  shape='pill'
                  width='100%'
                  disabled={!selectedChoiceKey}
                  handleClick={handleScoring}
                />
              )}
            </>
          )}
        </BottomContentContainer>
      </BottomContainer>
    </Wrapper>
  );
};

export default ClassroomConversationStudy;
